import { MAX_DAILY_PLAYS, type MyProfile, type RankingEntry, type RankingService } from '../api/ranking';
import type { Hud } from './Hud';

const TOP_DISPLAY_LIMIT = 20;
const RANKING_FETCH_LIMIT = 500;
const NICKNAME_SUCCESS_HINT_MS = 1500;

export async function showStartScreen(
  hud: Hud,
  ranking: RankingService,
  profile: MyProfile,
): Promise<void> {
  const entries = await ranking.fetchTop(RANKING_FETCH_LIMIT).catch(() => [] as RankingEntry[]);
  const isExhausted = profile.playsRemainingToday <= 0;
  hud.showOverlay(`
    <section class="hud-block">
      <h1>BLOOMY RUNNER</h1>
      <p>${controlsHint()}</p>
    </section>
    <section class="hud-block">
      ${renderHearts(profile.playsRemainingToday)}
    </section>
    <section class="hud-block">
      ${renderRankingSection(entries, profile.userId, '랭킹 TOP 20')}
    </section>
    <section class="hud-block">
      <p class="hud-start">${isExhausted ? exhaustedText() : startHint()}</p>
    </section>
  `);
}

function isTouchDevice(): boolean {
  return window.matchMedia('(pointer: coarse)').matches;
}

function controlsHint(): string {
  return isTouchDevice() ? '화면 터치로 점프' : 'SPACE / ↑ 점프 · ↓ 숙이기';
}

function startHint(): string {
  return isTouchDevice() ? '화면 터치로 시작하기' : 'SPACE / R 키로 시작하기';
}

function restartHint(): string {
  return isTouchDevice() ? '화면 터치로 다시 시작' : 'R 키로 다시 시작';
}

function exhaustedText(): string {
  return '오늘 플레이를 모두 사용했어요. 내일 다시 만나요!';
}

export async function showGameOverScreen(
  hud: Hud,
  ranking: RankingService,
  score: number,
  profile: MyProfile,
  rankings: ReadonlyArray<RankingEntry>,
  onProfileUpdated: (next: MyProfile) => void,
  onRankingsUpdated: (entries: ReadonlyArray<RankingEntry>) => void,
  successHint?: string,
): Promise<void> {
  const defaultNickname = profile.nickname ?? '';
  const isExhausted = profile.playsRemainingToday <= 0;
  const overlay = hud.showOverlay(`
    <section class="hud-block">
      <h2>GAME OVER</h2>
      <p>점수 ${score}</p>
    </section>
    <section class="hud-block">
      ${renderHearts(profile.playsRemainingToday)}
    </section>
    <section class="hud-block">
      ${renderRankingSection(rankings, profile.userId, '랭킹 TOP 5')}
    </section>
    <section class="hud-block">
      <form data-form="nickname">
        <input
          name="nickname"
          placeholder="닉네임"
          maxlength="20"
          autocomplete="off"
          value="${escapeHtml(defaultNickname)}"
        />
        <button type="submit">내 닉네임 변경</button>
      </form>
      <p class="hud-hint" data-form-hint></p>
    </section>
    <section class="hud-block">
      <p class="hud-start">${isExhausted ? exhaustedText() : restartHint()}</p>
    </section>
  `);
  if (successHint) {
    const hint = overlay.querySelector<HTMLElement>('[data-form-hint]');
    if (hint) {
      hint.textContent = successHint;
      hint.classList.add('hud-hint-success');
      setTimeout(() => {
        hint.textContent = '';
        hint.classList.remove('hud-hint-success');
      }, NICKNAME_SUCCESS_HINT_MS);
    }
  }
  bindNicknameForm(overlay, ranking, defaultNickname, async (nextProfile) => {
    onProfileUpdated(nextProfile);
    // 닉네임 변경 후 랭킹 재조회 (본인 row 의 nickname 갱신본을 받기 위해)
    // + main 의 allEntries 도 갱신해야 다음 라운드에서 stale 하지 않음.
    const refreshed = await ranking.fetchTop(RANKING_FETCH_LIMIT).catch(() => rankings.slice());
    onRankingsUpdated(refreshed);
    await showGameOverScreen(
      hud,
      ranking,
      score,
      nextProfile,
      refreshed,
      onProfileUpdated,
      onRankingsUpdated,
      '✓ 변경 완료',
    );
  });
}

function renderHearts(remaining: number): string {
  const clamped = Math.max(0, Math.min(MAX_DAILY_PLAYS, remaining));
  const items: string[] = [];
  for (let i = 0; i < MAX_DAILY_PLAYS; i += 1) {
    const filled = i < clamped;
    items.push(
      `<span class="hud-heart-large${filled ? ' is-filled' : ''}" aria-hidden="true">${heartSvgLarge(filled)}</span>`,
    );
  }
  return `<div class="hud-hearts-large">${items.join('')}</div>`;
}

// 7x6 클래식 도트 하트. Hud.ts 와 동일 패턴 — 사이즈만 CSS 로 다르게.
function heartSvgLarge(filled: boolean): string {
  const color = filled ? '#E94B6A' : '#C8C8C8';
  const cells: ReadonlyArray<readonly [number, number]> = [
    [1, 0], [2, 0], [4, 0], [5, 0],
    [0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [5, 1], [6, 1],
    [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2],
    [1, 3], [2, 3], [3, 3], [4, 3], [5, 3],
    [2, 4], [3, 4], [4, 4],
    [3, 5],
  ];
  const rects = cells
    .map(([x, y]) => `<rect x="${x}" y="${y}" width="1" height="1"/>`)
    .join('');
  return `<svg viewBox="0 0 7 6" fill="${color}" shape-rendering="crispEdges">${rects}</svg>`;
}

function renderRankingSection(
  entries: ReadonlyArray<RankingEntry>,
  myUserId: string,
  title: string,
): string {
  if (entries.length === 0) {
    return `<p class="hud-section-title">${title}</p><p class="hud-empty">아직 기록이 없습니다.</p>`;
  }
  const top = entries.slice(0, TOP_DISPLAY_LIMIT);
  // myUserId === '' (인증 실패 등) 이면 본인 매칭 X — 부작용 없이 TOP 만 표시.
  const myEntry = myUserId ? entries.find((entry) => entry.userId === myUserId) : undefined;
  const isMeInTop = myEntry !== undefined && top.some((entry) => entry.userId === myUserId);
  const renderItem = (entry: RankingEntry, isMe: boolean): string => `
        <li${isMe ? ' class="is-me"' : ''}>
          <span class="rank">${entry.rank}</span>
          <span class="player">
            <span class="company">${escapeHtml(entry.companyName)}</span>
            <span class="name">${escapeHtml(entry.nickname)}${isMe ? '<span class="me-badge">나</span>' : ''}</span>
          </span>
          <span class="score">${entry.score}</span>
        </li>`;
  const topItems = top.map((entry) => renderItem(entry, entry.userId === myUserId)).join('');
  // TOP 목록은 내부 스크롤 박스에 담고(높이 고정), 본인이 TOP 밖이면
  // 스크롤 영역 바깥에 본인 행을 고정 노출해 스크롤 없이도 내 등수가 보이게 한다.
  const topList = `<div class="ranking-scroll"><ol class="ranking">${topItems}</ol></div>`;
  const myRow = myEntry && !isMeInTop
    ? `<ol class="ranking ranking-me"><li class="ranking-divider" aria-hidden="true">⋯</li>${renderItem(myEntry, true)}</ol>`
    : '';
  return `<p class="hud-section-title">${title}</p>${topList}${myRow}`;
}

function bindNicknameForm(
  overlay: HTMLElement,
  ranking: RankingService,
  current: string,
  onChanged: (next: MyProfile) => Promise<void>,
): void {
  const form = overlay.querySelector<HTMLFormElement>('[data-form="nickname"]');
  if (!form) return;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    const nickname = String(formData.get('nickname') ?? '').trim();
    if (!nickname || nickname === current) return;
    const button = form.querySelector('button');
    const originalLabel = button?.textContent ?? '';
    if (button) {
      button.disabled = true;
      button.textContent = '변경 중...';
    }
    try {
      const next = await ranking.updateNickname(nickname);
      // 성공 시점에 overlay 가 곧 재렌더링되므로 별도 success 토스트 불필요
      // — 재렌더된 form 의 .hud-hint 로 1.5 초만 표기 (showGameOverScreen 안에서 처리).
      await onChanged(next);
    } catch (error) {
      const hint = form.parentElement?.querySelector<HTMLElement>('[data-form-hint]');
      if (hint) hint.textContent = `변경 실패: ${(error as Error).message}`;
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  });
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (ch) => map[ch] ?? ch);
}
